import { useCallback, useEffect, useState } from "react";

import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Stack, Typography } from "@mui/material";

import { useOutletApi } from "@/features/authentication";
import {
  CONVERT_PURCHASE_RETURN_DEFAULT_VALUES,
  CONVERT_PURCHASE_RETURN_VALIDATION_SCHEMA,
  ConvertPurchaseReturnEditableFieldsSection,
  ConvertPurchaseReturnGeneralInformationSection,
  ConvertPurchaseReturnProductVariantsSection,
  INCOMING_PURCHASE_RETURNS_CHANGED_EVENT,
} from "@/features/purchase-returns";
import { CenteredLoading, PageContainer, useSnackbar } from "@/features/ui";
import { getErrorMessage } from "@/features/shared";

export const CreateSalesReturnFromPurchaseReturn = () => {
  const { purchaseReturnReferenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const api = useOutletApi();
  const [purchaseReturn, setPurchaseReturn] = useState(null);
  const [loading, setLoading] = useState(true);

  const methods = useForm({
    defaultValues: CONVERT_PURCHASE_RETURN_DEFAULT_VALUES,
    resolver: yupResolver(CONVERT_PURCHASE_RETURN_VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState } = methods;

  const loadPurchaseReturn = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await api.get(
        `purchase-returns/incoming/${purchaseReturnReferenceNumber}`,
        {
          params: {
            withPurchaseReturnExpenses: true,
          },
        }
      );
      setPurchaseReturn(data);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat retur pembelian"));
    } finally {
      setLoading(false);
    }
  }, [api, purchaseReturnReferenceNumber, showSnackbar]);

  useEffect(() => {
    loadPurchaseReturn();
  }, [loadPurchaseReturn]);

  const onSubmit = async (formData) => {
    try {
      const payload = {
        transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
        note: formData.note?.trim() || null,
        resend: !!formData.resend,
      };

      const {
        data: { data },
      } = await api.post(
        `purchase-returns/${purchaseReturnReferenceNumber}/sales-returns`,
        payload
      );

      // This return just left the queue; drop the drawer badge now rather than
      // at the next poll.
      window.dispatchEvent(new Event(INCOMING_PURCHASE_RETURNS_CHANGED_EVENT));

      showSnackbar(
        data.replacement
          ? `Retur penjualan ${data.referenceNumber} dibuat, pesanan pengganti ${data.replacement.purchaseOrderReferenceNumber} / ${data.replacement.salesOrderReferenceNumber}`
          : `Retur penjualan ${data.referenceNumber} berhasil dibuat`,
        "success"
      );
      navigate(`/sales/sales-returns/${data.referenceNumber}`);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal menyimpan data")
      );
      throw error;
    }
  };

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (!purchaseReturn) {
    return (
      <Typography variant="h6" color="error">
        Retur pembelian tidak ditemukan
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Buat Retur Penjualan dari Retur Pembelian"
      breadcrumbs={[
        {
          title: "Retur dari Pembeli",
          path: "/sales/incoming-purchase-returns",
        },
        { title: purchaseReturnReferenceNumber },
        { title: "Buat Retur Penjualan" },
      ]}
    >
      <Stack spacing={2}>
        <Alert severity="info">
          Retur penjualan dibuat persis seperti yang dinyatakan outlet peretur —
          100%, tanpa penerimaan sebagian. Selisih fisik dikoreksi setelahnya
          lewat dokumen koreksi.
        </Alert>

        <ConvertPurchaseReturnGeneralInformationSection
          purchaseReturn={purchaseReturn}
        />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2}>
              <ConvertPurchaseReturnEditableFieldsSection />

              <ConvertPurchaseReturnProductVariantsSection
                purchaseReturnProductVariants={
                  purchaseReturn.purchaseReturnProductVariants
                }
              />

              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: "flex-end" }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={formState.isSubmitting}
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  loading={formState.isSubmitting}
                >
                  Buat Retur Penjualan
                </Button>
              </Stack>
            </Stack>
          </form>
        </FormProvider>
      </Stack>
    </PageContainer>
  );
};
